import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { errorToast, successToast } from "@telluscompany/design-system";

import { useLocalization } from "@i18n";
import { axiosDeleteData, axiosPostData } from "@shared/api/clients";

import { ADMIN_COMPLEX_ADMINS_QUERY_KEY } from "./complexAdmins.queries";
import { AssignComplexAdminRequest } from "./complexAdmins.types";

const COMPLEX_ADMIN_BASE = (complexId: string) =>
  `v1/admin/complexes/${complexId}/complex-admins`;

export const assignComplexAdmin = async ({
  complexId,
  userId,
}: AssignComplexAdminRequest): Promise<void> => {
  await axiosPostData<void, { userId: string }>(COMPLEX_ADMIN_BASE(complexId), {
    userId,
  });
};

export const removeComplexAdmin = async ({
  complexId,
  assignmentId,
}: {
  complexId: string;
  assignmentId: string;
}): Promise<void> => {
  await axiosDeleteData<void>(
    `${COMPLEX_ADMIN_BASE(complexId)}/${assignmentId}`,
  );
};

const invalidate = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({
    queryKey: ADMIN_COMPLEX_ADMINS_QUERY_KEY,
    exact: false,
  });
};

export const useAssignComplexAdminMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: assignComplexAdmin,
    onSuccess: () => {
      invalidate(queryClient);
      successToast({ description: translate("common.createSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorCreating"),
      });
    },
  });
};

export const useRemoveComplexAdminMutation = () => {
  const queryClient = useQueryClient();
  const { translate } = useLocalization();

  return useMutation({
    mutationFn: removeComplexAdmin,
    onSuccess: () => {
      invalidate(queryClient);
      successToast({ description: translate("common.deleteSuccess") });
    },
    onError: (error: AxiosError) => {
      errorToast({
        description: error.parsedMessage ?? translate("common.errorDeleting"),
      });
    },
  });
};
